/*----------------------------------
- ROUTE: /tld
----------------------------------*/

/**
 * @docs docs/features/tld-intelligence
 * @adr  ADR-0054
 * @adr  ADR-0063
 * @rule Static and publication-managed (D28): `DomainsWarmup` republishes it when the public set
 *       changes, and neither `data` nor `render` reads the query string, because a static page
 *       is cached by path whatever the query.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { definePageRoute } from '@common/router/definitions';
import type { TFetcher } from '@common/router/request/api';
import { TLD_HUB_PATH, tldInsightPath, type PublicTldBoard } from '@uniquedomains/components/contracts/tlds/public';
import { TLD_HUB_PAGE_MIRROR_PATH } from '@uniquedomains/core/domains/tlds';

import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { TLD_PAGE_CONTENT } from '@/client/catalogs/landing/tld/pageContent';
import { buildTldHubStructuredData, type WebsiteJsonLdNode } from '@/client/pages/landing/seo/structuredData';
import { PUBLIC_INDEX_ROBOTS_DIRECTIVES } from '@/common/catalogs/crawlerPolicy';
import { cachedLandingHeaderNavigation } from '@/common/libs/connected/publicProductReadCache';
import TldHubPage from './page/hub/TldHubPage';

/*----------------------------------
- TYPES
----------------------------------*/

type TldHubRouteDataContext = {
    Product: {
        Navigation: { GetLandingHeaderNavigation: () => TFetcher<LandingHeaderDomainsNavigation> };
        Tlds: { GetPublicBoard: () => TFetcher<PublicTldBoard> };
    };
};

type TldHubRouteRenderContext = {
    enforceDomainPublicationLinks: boolean;
    board: PublicTldBoard;
    navigation: LandingHeaderDomainsNavigation;
    page: {
        description?: string;
        head: Array<{ $: string; [key: string]: string | Date | null | undefined }>;
        jsonld: WebsiteJsonLdNode[];
        metas: Record<string, string | Date | null | undefined>;
        title?: string;
        url: string;
    };
    renderedDomainResourcePaths: readonly string[];
    Router: { url: (path: string) => string };
};

/*----------------------------------
- PAGE
----------------------------------*/

export default definePageRoute({
    path: '/tld',
    options: {
        auth: false,
        layout: false,
        static: {
            // Same interval as its `PUBLICATION_MANAGED_STATIC_PAGES` entry in DomainsWarmup. The
            // figures move once a night; a public-set change republishes it at once from there.
            refresh: '1 hour',
            urls: ['/tld'],
        },
    },
    data: ({ Product }: TldHubRouteDataContext) => ({
        navigation: cachedLandingHeaderNavigation.read() ?? Product.Navigation.GetLandingHeaderNavigation(),
        board: Product.Tlds.GetPublicBoard(),
    }),
    render: ({
        enforceDomainPublicationLinks,
        board,
        navigation,
        page,
        renderedDomainResourcePaths,
        Router,
    }: TldHubRouteRenderContext) => {
        const { pageTitle, metaDescription, markdownTitle } = TLD_PAGE_CONTENT.hub.meta;
        const canonicalUrl = Router.url(TLD_HUB_PATH);
        // The markup lists the same insight pages the board links: public-set rows, published ones
        // only in prod.
        const publishedPaths = enforceDomainPublicationLinks ? new Set(renderedDomainResourcePaths) : null;
        const listedEntries = board.rows.filter((row) => row.inPublicSet && (!publishedPaths || publishedPaths.has(tldInsightPath(row.slug))));

        page.title = pageTitle;
        page.description = metaDescription;
        page.url = canonicalUrl;
        page.metas = {
            ...page.metas,
            'og:description': metaDescription,
            'og:title': pageTitle,
            'og:type': 'website',
            'og:url': canonicalUrl,
            'twitter:card': 'summary_large_image',
            'twitter:description': metaDescription,
            'twitter:title': pageTitle,
            'twitter:url': canonicalUrl,
        };
        page.head.push({ $: 'meta', name: 'robots', content: PUBLIC_INDEX_ROBOTS_DIRECTIVES });
        page.head.push({
            $: 'link',
            rel: 'alternate',
            type: 'text/markdown',
            title: markdownTitle,
            href: Router.url(TLD_HUB_PAGE_MIRROR_PATH),
        });
        page.jsonld.push(
            ...buildTldHubStructuredData({
                canonicalUrl,
                description: metaDescription,
                entries: listedEntries.map((entry) => ({ slug: entry.slug, url: Router.url(tldInsightPath(entry.slug)) })),
                title: pageTitle,
            }),
        );

        return <TldHubPage board={board} navigation={navigation} />;
    },
});
